import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search } from 'lucide-react';
import { NetworkStatusIndicator } from '@/components/flexe/NetworkStatusIndicator';
import FlexEHistoryFilters from '@/components/flexe/FlexEHistoryFilters';
import FlexEBulkActions from '@/components/flexe/FlexEBulkActions';
import FlexEHistoryTable from '@/components/flexe/FlexEHistoryTable';
import { useFlexESimulations, FlexESimulationSummary } from '@/hooks/useFlexESimulations';

export default function CRMFlexE() {
  const navigate = useNavigate();
  
  // Filters state
  const [searchQuery, setSearchQuery] = useState('');
  const [successRateRange, setSuccessRateRange] = useState<[number, number]>([0, 100]);
  const [onlyFailed, setOnlyFailed] = useState(false);
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const {
    simulations,
    isLoading,
    deleteSimulation,
    bulkDeleteSimulations,
    renameSimulation,
    isDeleting,
    isRenaming
  } = useFlexESimulations({
    search: searchQuery,
    minSuccessRate: successRateRange[0],
    maxSuccessRate: successRateRange[1],
    onlyFailed
  });

  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAll = () => {
    if (selectedIds.length === simulations.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(simulations.map(s => s.id));
    }
  };

  const handleBulkDelete = () => {
    bulkDeleteSimulations(selectedIds);
    setSelectedIds([]);
  };

  const handleView = (sim: FlexESimulationSummary) => {
    navigate(`/crm/flexe/${sim.id}`);
  };

  const handleRename = (sim: FlexESimulationSummary) => {
    const newName = prompt('Nieuwe naam voor simulatie:', sim.name);
    if (newName && newName.trim()) {
      renameSimulation({ simId: sim.id, newName: newName.trim() });
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Flex-E Simulaties</h1>
          <p className="text-muted-foreground mt-2">
            Beheer en bekijk alle batterij simulaties
          </p>
        </div>
        <Button onClick={() => navigate('/crm/flexe/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Nieuwe Simulatie
        </Button>
      </div>

      <NetworkStatusIndicator />

      {/* Search & Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Zoek simulaties..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <FlexEHistoryFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          successRateRange={successRateRange}
          onSuccessRateChange={setSuccessRateRange}
          onlyFailed={onlyFailed}
          onOnlyFailedChange={setOnlyFailed}
        />
      </div>

      {/* Bulk Actions */}
      <FlexEBulkActions
        selectedCount={selectedIds.length}
        onClearSelection={() => setSelectedIds([])}
        onBulkDelete={handleBulkDelete}
        isDeleting={isDeleting}
      />

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Simulaties laden...</p>
        </div>
      ) : simulations.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Geen simulaties gevonden</p>
          <Button onClick={() => navigate('/crm/flexe/new')}>
            <Plus className="w-4 h-4 mr-2" />
            Maak je eerste simulatie
          </Button>
        </div>
      ) : (
        <FlexEHistoryTable
          simulations={simulations}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelect}
          onToggleSelectAll={handleToggleSelectAll}
          onView={handleView}
          onRename={handleRename}
          onDelete={deleteSimulation}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
}
